import { all, call, put, takeLatest } from '@redux-saga/core/effects';
import axios from 'axios';

import {
  requestCreateSystemUser,
  requestViewSystemUser,
  requestUpdateSystemUser,
  requestInitiatePasswordReset,
  requestResetPassword,
  requestDeleteAccount
} from './system-user.actions';
import {
  getCreateSystemUserRequest,
  getViewSystemUserRequest,
  getUpdateSystemUserRequest,
  getInitiatePasswordResetRequest,
  getResetPasswordRequest,
  getDeleteAccountRequest
} from './system-user.endpoints';

import { addSystemNotice, addSpinner, removeSpinner, setUser } from '../../slices/system/system.slice';

import { SUCCESS, ERROR } from '../../utils/constants';
import { getErrorMessage } from '../../utils/api-util';
import { retrieveFromLocalStorage } from '../../utils/util';

export function* performCreateSystemUserRequest({ payload }) {
  const { input, onSuccess } = payload;

  try {
    yield put(addSpinner('Creating User'));

    const [endpoint, options] = getCreateSystemUserRequest(input);

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, data.programmeName);
    }

    yield put(removeSpinner('Creating User'));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(removeSpinner('Creating User'));
    yield put(addSystemNotice({ type: ERROR, message }));
  }
}

export function* watchForCreateSystemUserRequest() {
  yield takeLatest(requestCreateSystemUser.type, performCreateSystemUserRequest);
}

export function* performViewSystemUserRequest({ payload }) {
  const { externalReference, onSuccess } = payload;

  try {
    yield put(addSpinner('Retrieving User'));

    const [endpoint, options] = getViewSystemUserRequest(externalReference);

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    yield put(setUser(data.user));
    yield put(removeSpinner('Retrieving User'));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(removeSpinner('Retrieving User'));
    yield put(addSystemNotice({ type: ERROR, message }));
  }
}

export function* watchForViewSystemUserRequest() {
  yield takeLatest(requestViewSystemUser.type, performViewSystemUserRequest);
}

export function* performUpdateSystemUserRequest({ payload }) {
  const { input, onSuccess } = payload;

  try {
    yield put(addSpinner('Updating User'));

    const [endpoint, options] = getUpdateSystemUserRequest(input);

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    yield put(removeSpinner('Updating User'));
    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
    yield put(requestViewSystemUser({ externalReference: input.externalReference }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(removeSpinner('Updating User'));
    yield put(addSystemNotice({ type: ERROR, message }));
  }
}

export function* watchForUpdateSystemUserRequest() {
  yield takeLatest(requestUpdateSystemUser.type, performUpdateSystemUserRequest);
}

export function* performInitiatePasswordResetRequest({ payload }) {
  const { email, mobileNumber, onSuccess } = payload;

  try {
    yield put(addSpinner('Initiating Password Reset'));

    const [endpoint, options] = getInitiatePasswordResetRequest(email, mobileNumber);

    yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    yield put(removeSpinner('Initiating Password Reset'));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(removeSpinner('Initiating Password Reset'));
    yield put(addSystemNotice({ type: ERROR, message }));
  }
}

export function* watchForInitiatePasswordResetRequest() {
  yield takeLatest(requestInitiatePasswordReset.type, performInitiatePasswordResetRequest);
}

export function* performResetPasswordRequest({ payload }) {
  const { input, onSuccess } = payload;

  try {
    yield put(addSpinner('Resetting Password'));

    const [endpoint, options] = getResetPasswordRequest(input);

    yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    yield put(removeSpinner('Resetting Password'));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(removeSpinner('Resetting Password'));
    yield put(addSystemNotice({ type: ERROR, message }));
  }
}

export function* watchForResetPasswordRequest() {
  yield takeLatest(requestResetPassword.type, performResetPasswordRequest);
}

export function* performDeleteAccountRequest({ payload }) {
  const { onSuccess } = payload;

  try {
    yield put(addSpinner('Deleting Account'));

    const systemUserExternalReference = retrieveFromLocalStorage('user');
    const [endpoint, options] = getDeleteAccountRequest(systemUserExternalReference);

    yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    yield put(removeSpinner('Deleting Account'));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(removeSpinner('Deleting Account'));
    yield put(addSystemNotice({ type: ERROR, message }));
  }
}

export function* watchForDeleteAccountRequest() {
  yield takeLatest(requestDeleteAccount.type, performDeleteAccountRequest);
}

export default function* systemUserSaga() {
  yield all([
    watchForCreateSystemUserRequest(),
    watchForViewSystemUserRequest(),
    watchForUpdateSystemUserRequest(),
    watchForInitiatePasswordResetRequest(),
    watchForResetPasswordRequest(),
    watchForDeleteAccountRequest()
  ]);
}
